/**
 * Registry of live Firestore listener re-subscribe callbacks (ELARA-695).
 *
 * `createClientQuery` / `createClientDocument` each own a cached `onSnapshot`
 * stream. When a mobile OS suspends sockets on background, that stream can stall
 * silently — it neither errors (so the built-in retry never kicks in) nor
 * delivers, leaving the listener-driven UI stale on resume. Each live handle
 * registers a re-subscribe callback here; the lifecycle monitor (driven by the
 * platform `LifecyclePort`) calls {@link resubscribeActiveListeners} on resume
 * to tear down and re-establish every active stream.
 *
 * Re-subscription is idempotent: each callback stops its existing stream before
 * opening a new one (see the `subscribe()` closures in the two factories), so
 * repeated resume signals never leak listeners.
 */

const resubscribers = new Set<() => void>();

/**
 * Registers a live listener's re-subscribe callback. Returns an unregister
 * function the handle MUST call from its `unsubscribe`/dispose path so disposed
 * listeners are not re-subscribed on the next resume.
 */
export function registerActiveListener(resubscribe: () => void): () => void {
  resubscribers.add(resubscribe);
  return () => {
    resubscribers.delete(resubscribe);
  };
}

/**
 * Re-establishes every active Firestore listener. Safe to call repeatedly; a
 * failing callback is logged and skipped so one bad listener cannot block the
 * rest from recovering.
 */
export function resubscribeActiveListeners(): void {
  if (resubscribers.size === 0) return;
  console.debug(`[listener-registry] Re-subscribing ${resubscribers.size} active Firestore listener(s)`);
  // Snapshot the set: a re-subscribe callback could, in theory, register/unregister.
  for (const resubscribe of [...resubscribers]) {
    try {
      resubscribe();
    } catch (error) {
      console.warn('[listener-registry] Failed to re-subscribe a listener (non-fatal)', error);
    }
  }
}

/** Number of currently registered live listeners (diagnostics / tests). */
export function getActiveListenerCount(): number {
  return resubscribers.size;
}
